import { OPR_SESSION_TOKEN } from "@/constants";
import request, { _POST } from "@/services/request";
import { createFrameTransformStream } from "@/services/stream";

export type QunjielongAccountItem = {
  id: string;
  name: string;
  appId: string;
  companyId: string;
  uid: string;
  version: string;
  clientVersion: string;
  enabled: boolean;
  remark: string;
  createdAt: string;
  updatedAt: string;
  lastUsedAt: string;
  lastError: string;
  lastErrorAt: string;
  hasAuthorization: boolean;
};

export type QunjielongAccountListReq = {
  page?: number;
  size?: number;
  name?: string;
  companyId?: string;
  enabled?: boolean;
};

export type QunjielongAccountListRsp = {
  list: QunjielongAccountItem[];
  total: string;
};

export type QunjielongAccountSaveReq = {
  name: string;
  appId: string;
  authorization: string;
  companyId: string;
  uid: string;
  version: string;
  clientVersion: string;
  enabled: boolean;
  remark?: string;
};

export type QunjielongAccountUpdateReq = QunjielongAccountSaveReq & {
  id: string;
};

export type QunjielongAccountUpdateEnabledReq = {
  id: string;
  enabled: boolean;
};

export type QunjielongSearchReq = {
  page?: number;
  size?: number;
  keyword: string;
  lastRowKeyId?: string;
  lastRowKey?: string;
};

export type QunjielongSearchActItem = {
  actId?: number | string;
  activityName?: string;
  activityStatus?: number;
  actInfoTextStr?: string;
  picList?: string[];
  timeTag?: string;
  participationCount?: number;
  viewCount?: number;
  feedGroupSaleChildActDTO?: {
    priceTips?: string;
    orderCount?: number;
    viewCount?: number;
  };
};

export type QunjielongSearchFeedItem = {
  flowKey?: number | string;
  actItemDTO?: QunjielongSearchActItem;
};

export type QunjielongSearchRsp = {
  feedItemDTOList?: QunjielongSearchFeedItem[];
  hasNextPage?: boolean;
  isOfficialRecommend?: boolean;
  lastRowKeyId?: string;
};

type QunjielongSearchStreamBaseEvent = {
  traceId?: string;
  completed?: number;
  failed?: number;
  total?: number;
};

export type QunjielongSearchStreamSearchEvent = QunjielongSearchStreamBaseEvent & {
  type: "search";
  data?: QunjielongSearchRsp;
};

export type QunjielongSearchStreamRecordEvent = QunjielongSearchStreamBaseEvent & {
  type: "record";
  flowKey: string;
  replacements?: Record<string, unknown>;
};

export type QunjielongSearchStreamRecordErrorEvent = QunjielongSearchStreamBaseEvent & {
  type: "record_error";
  flowKey: string;
  replacements?: Record<string, unknown>;
  error?: string;
};

export type QunjielongSearchStreamDoneEvent = QunjielongSearchStreamBaseEvent & {
  type: "done";
};

export type QunjielongSearchStreamErrorEvent = QunjielongSearchStreamBaseEvent & {
  type: "error";
  code?: number;
  msg?: string;
};

export type QunjielongSearchStreamEvent =
  | QunjielongSearchStreamSearchEvent
  | QunjielongSearchStreamRecordEvent
  | QunjielongSearchStreamRecordErrorEvent
  | QunjielongSearchStreamDoneEvent
  | QunjielongSearchStreamErrorEvent;

export type QunjielongDetailReq = {
  activityId: string;
  sceneFlow?: string;
};

export type QunjielongActivityDetailMedia = {
  content?: string;
  mediaType?: "text" | "txt" | "singleImg" | "multiImg" | "video" | "tag" | "goodsInfo" | "img" | string;
  contentList?: QunjielongActivityDetailMedia[];
  goodsInfo?: {
    goodsId?: number;
    goodsInfoDetails?: QunjielongActivityDetailMedia[];
  };
  qjlImageInfo?: {
    height?: number;
    width?: number;
  };
};

export type QunjielongDetailTaskStatus = "running" | "success" | "failed";

export type QunjielongActivityDetailData = {
  activity?: QunjielongSearchActItem & {
    effectTimeStr?: string;
    issuerNickName?: string;
    minPrice?: number;
    maxPrice?: number;
  };
  activityGhHome?: {
    fansCount?: number;
    ghIntro?: string;
    ghName?: string;
    ghOrderNum?: string;
    logoUrl?: string;
  };
  activityInfo?: {
    activityDetail?: {
      detail?: QunjielongActivityDetailMedia[];
    };
  };
  activityStatInfo?: {
    orderCount?: number;
    totalBuyerNumForC?: number;
    viewCountForC?: number;
  };
  servicesGuarantee?: {
    customerServiceRemark?: string;
    deliveryDeadline?: number;
    deliveryPlace?: string;
  };
};

export type QunjielongActivityDetailTask = {
  taskId?: string;
  status?: QunjielongDetailTaskStatus;
  activityId?: string;
  error?: string;
  createdAt?: number;
  updatedAt?: number;
  data?: QunjielongActivityDetailData;
} & QunjielongActivityDetailData;

export type QunjielongActivityDetailRsp = QunjielongActivityDetailTask;

type QunjielongDetailStreamBaseEvent = {
  traceId?: string;
  completed?: number;
  failed?: number;
  total?: number;
};

export type QunjielongDetailStreamDetailEvent = QunjielongDetailStreamBaseEvent & {
  type: "detail";
  data?: QunjielongActivityDetailData;
};

export type QunjielongDetailStreamRecordEvent = QunjielongDetailStreamBaseEvent & {
  type: "record";
  replacements?: Record<string, unknown>;
};

export type QunjielongDetailStreamRecordErrorEvent = QunjielongDetailStreamBaseEvent & {
  type: "record_error";
  error?: string;
};

export type QunjielongDetailStreamDoneEvent = QunjielongDetailStreamBaseEvent & {
  type: "done";
};

export type QunjielongDetailStreamErrorEvent = QunjielongDetailStreamBaseEvent & {
  type: "error";
  code?: number;
  msg?: string;
};

export type QunjielongDetailStreamEvent =
  | QunjielongDetailStreamDetailEvent
  | QunjielongDetailStreamRecordEvent
  | QunjielongDetailStreamRecordErrorEvent
  | QunjielongDetailStreamDoneEvent
  | QunjielongDetailStreamErrorEvent;

export function getQunjielongSearchStreamUrl() {
  const baseURL = process.env.ENV_REPLACE_API_URL?.trim();
  if (!baseURL) {
    throw new Error("未配置群接龙搜索服务地址");
  }
  return `${baseURL.replace(/\/$/, "")}/admin/qunjielong/search/stream`;
}

export function getQunjielongDetailStreamUrl() {
  const baseURL = process.env.ENV_REPLACE_API_URL?.trim();
  if (!baseURL) {
    throw new Error("未配置群接龙详情服务地址");
  }
  return `${baseURL.replace(/\/$/, "")}/admin/qunjielong/detail/stream`;
}

export function getQunjielongAuthHeader() {
  return window.localStorage.getItem(OPR_SESSION_TOKEN) || "";
}

export function createQunjielongSearchNdjsonTransformStream() {
  return createFrameTransformStream<QunjielongSearchStreamEvent>("\n", (line) => {
    const event = parseQunjielongStreamEvent(line);
    if (!event || !isQunjielongSearchStreamEvent(event)) return null;
    return event;
  });
}

/** 详情流与搜索流共用同一套 NDJSON 解析，仅按事件类型区分 */
export function createQunjielongDetailNdjsonTransformStream() {
  return createFrameTransformStream<QunjielongDetailStreamEvent>("\n", (line) => {
    const event = parseQunjielongStreamEvent(line);
    if (!event || !isQunjielongDetailStreamEvent(event)) return null;
    return event;
  });
}

/** 搜索流不会下发 detail 事件 */
function isQunjielongSearchStreamEvent(
  event: QunjielongSearchStreamEvent | QunjielongDetailStreamEvent
): event is QunjielongSearchStreamEvent {
  return event.type !== "detail";
}

/** 详情流不会下发 search 事件 */
function isQunjielongDetailStreamEvent(
  event: QunjielongSearchStreamEvent | QunjielongDetailStreamEvent
): event is QunjielongDetailStreamEvent {
  return event.type !== "search";
}

function parseQunjielongStreamEvent(line: string): QunjielongSearchStreamEvent | QunjielongDetailStreamEvent | null {
  const content = line.trim();
  if (!content) return null;
  try {
    const parsed = JSON.parse(content) as Record<string, unknown>;
    if (
      parsed.type === "detail" ||
      parsed.type === "search" ||
      parsed.type === "record" ||
      parsed.type === "record_error" ||
      parsed.type === "done" ||
      parsed.type === "error"
    ) {
      return parsed as QunjielongSearchStreamEvent | QunjielongDetailStreamEvent;
    }
    return null;
  } catch {
    return null;
  }
}

const listQunjielongAccounts = (params?: QunjielongAccountListReq) =>
  request<QunjielongAccountListRsp, QunjielongAccountListReq>(_POST, "/admin/qunjielong/account/list")(params ?? {});

const addQunjielongAccount = (params: QunjielongAccountSaveReq) =>
  request<null, QunjielongAccountSaveReq>(_POST, "/admin/qunjielong/account/add")(params);

const updateQunjielongAccount = (params: QunjielongAccountUpdateReq) =>
  request<null, QunjielongAccountUpdateReq>(_POST, "/admin/qunjielong/account/update")(params);

const updateQunjielongAccountEnabled = (params: QunjielongAccountUpdateEnabledReq) =>
  request<null, QunjielongAccountUpdateEnabledReq>(_POST, "/admin/qunjielong/account/updateEnabled")(params);

const searchQunjielongActivities = (params: QunjielongSearchReq) =>
  request<QunjielongSearchRsp, QunjielongSearchReq>(_POST, "/admin/qunjielong/search")(params);

const getQunjielongActivityDetail = (params: QunjielongDetailReq) =>
  request<QunjielongActivityDetailRsp, QunjielongDetailReq>(_POST, "/admin/qunjielong/detail")(params);

const qunjielongAccountApis = {
  listQunjielongAccounts,
  addQunjielongAccount,
  updateQunjielongAccount,
  updateQunjielongAccountEnabled,
  searchQunjielongActivities,
  getQunjielongSearchStreamUrl,
  getQunjielongDetailStreamUrl,
  getQunjielongAuthHeader,
  createQunjielongSearchNdjsonTransformStream,
  createQunjielongDetailNdjsonTransformStream,
  getQunjielongActivityDetail,
};

export default qunjielongAccountApis;
